import { useId, useState } from 'react';
import Link from 'next/link';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useLocale } from '../../../lib/locale-context';
import { useQuery } from '@tanstack/react-query';
import { groupByDay, meetingFileVfsPath, useMeetingHistory, type PastMeeting, type PastMeetingNote } from '../../../lib/meetings-channel';
import { parseMeetingNotes } from '../../../lib/meeting-notes-card';
import { queryKeys } from '../../../lib/query-client';
import { fetchWorkspaceFile } from '../../../lib/workspace-files';
import { meetingClock, meetingDayHeading, meetingDuration, meetingStamp } from '../../../lib/meeting-time';
import { parseFileRecordContent } from '../../../lib/file-record';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { Collapsible, SelectionIndicator, TextButton, Typography } from '../ui';
import styles from './MeetingHistorySection.module.css';

const PAGE = 'settings-connectors';

// Past meetings — the Meetings connection view's "Past meetings" tab.
//
// A section of its own rather than rows in Activity: `activity` is strictly task fields, and an action
// item is a proposal until the user says yes. Feeding meetings into Activity would mean creating tasks
// for things nobody agreed to.
//
// The tab is the heading, so this draws none. It mounts only while its tab is open, which is what keeps
// the history read off every visit that never looks at it.
//
// Rows sit under their day, newest first. A meeting her session dropped out of has a row too, marked
// "No notes": it is where the user looks for those notes, and the row opens the thread that says why.
//
// **A ROW IS ONE LINE, and its action items are behind a count that opens in place.** The count
// opens rather than merely counting because this section is the ONLY place those fields survive as
// structure: `renderMeetingSummary` flattens an action item's owner and due date into a parenthetical
// sentence on the way into the write-up conversation, so the conversation cannot give them back.
//
// `kind: 'note'` notes are deliberately not here. The conversation is where the whole write-up lives,
// and a row's job is to get you to it, not to reproduce it.

/**
 * The MEETING's own start, never the write-up's: a write-up lands minutes after the room empties, and
 * on a slow day an hour after — a row stamped with it is the wrong meeting to a reader looking for the
 * 10am. `recordedAt` stays as the fallback for a row from before the span was recorded.
 */
function meetingStartedAt(meeting: PastMeeting): number {
  return meetingStamp(meeting.startedAt) ?? meeting.recordedAt;
}

/** Owner and due date as a trailing line of DATA — a name and a date need no label to read. */
function attribution(note: Pick<PastMeetingNote, 'owner' | 'due'>): string {
  return [note.owner, note.due].filter(Boolean).join(' · ');
}

/**
 * Which items are DONE lives in `notes.md`, not in the history row: the row is a snapshot taken when the
 * meeting ended, and a tick is an edit to the file (see `lib/meeting-notes-card.ts`). So a row with
 * action items reads the file once, cached, and shows its items with their ticks; a row whose file
 * cannot be read keeps the snapshot's items and simply says nothing about done. Every row with a notes
 * file spends the request — not only rows whose snapshot had items, because the reader can ADD an item
 * by editing the file, and a row gated on its snapshot would never learn of it.
 *
 * The ticks are SHOWN here and never changed here: ticking an item, and turning one into a task, happen
 * on the card in the meeting's conversation.
 *
 * The cost, stated plainly: the count is drawn COLLAPSED, so the read is eager — a page of ten rows is
 * up to ten small `/api/workspace-file` reads, in parallel, each cached five minutes and shared with
 * the card in the conversation (same `queryKeys.workspaceFile` entry). The history row was meant to make
 * this list one query, and it still is for everything but the items' current state, which only the file
 * knows. If that proves too much, the honest alternative is the harness re-reading the file into the
 * row, not a count that hides behind the expander.
 */
function useMeetingNotesItems(meeting: PastMeeting) {
  const vfsPath = meetingFileVfsPath(meeting.notesPath);
  const query = useQuery({
    queryKey: queryKeys.workspaceFile(vfsPath),
    queryFn: () => fetchWorkspaceFile(vfsPath as string),
    enabled: !!vfsPath,
    staleTime: 5 * 60_000,
  });
  // An empty file read means "not there" (the route answers '' for a missing file) → the snapshot. A file
  // that READ but parses to no items is a real answer — the reader deleted them — and must not fall back
  // to a snapshot that still lists them.
  const body = query.data ? parseFileRecordContent(query.data).body : '';
  return body.trim() ? parseMeetingNotes(body).actionItems : null;
}

function MeetingHistoryRow({ meeting, locale, initiallyOpen }: { meeting: PastMeeting; locale: string; initiallyOpen: boolean }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(initiallyOpen);
  const panelId = useId();
  const snapshot = meeting.notes.filter((note) => note.kind === 'action_item');
  const fromFile = useMeetingNotesItems(meeting);
  const actions: { text: string; owner?: string; due?: string; done?: boolean }[] = fromFile ?? snapshot;
  const done = actions.filter((note) => note.done).length;
  const started = meetingStartedAt(meeting);
  const startedIso = new Date(started).toISOString();
  // The day is the group's heading, so the row carries the clock.
  const when = meetingClock(started, locale);
  const duration = meetingDuration(meeting.startedAt, meeting.endedAt);
  const durationLabel = duration
    ? duration.hours > 0
      ? t('meetings.duration.hoursMinutes', { hours: duration.hours, minutes: duration.minutes })
      : t('meetings.duration.minutes', { count: duration.minutes })
    : '';

  return (
    <li className={styles.row}>
      <div className={styles.rowHead}>
        {/* The whole write-up is a conversation, so the row opens it rather than re-rendering it here:
            one conversation query param across the app (`?conversation=<id>`). */}
        <Link
          className={styles.rowLink}
          href={{ pathname: '/', query: { conversation: meeting.conversationId, chat: 'expanded' } }}
          onClick={() => logPageClickBi({ page_name: PAGE, button_name: 'open_meeting_writeup', conversation_id: meeting.conversationId })}
        >
          <Typography variant="p1" color="primary" component="span" className={styles.title}>
            {meeting.title ?? t('connectors.meetings.untitled')}
          </Typography>
          <time className={styles.when} dateTime={startedIso}>
            {durationLabel ? `${when} · ${durationLabel}` : when}
          </time>
        </Link>

        {/* A sibling of the link, never inside it: a button nested in an anchor is invalid, and the two
            genuinely go different places — one opens the conversation, one opens the list in place. */}
        {meeting.noNotes || actions.length === 0 ? (
          <Typography variant="p3" color="secondary" component="span" className={styles.rowTrail}>
            {t(meeting.noNotes ? 'connectors.meetings.past.noNotes' : 'connectors.meetings.past.noActions')}
          </Typography>
        ) : (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => {
              logPageClickBi({ page_name: PAGE, button_name: 'toggle_meeting_action_items', answer: open ? 'collapse' : 'expand' });
              setOpen((o) => !o);
            }}
          >
            <ChevronDown size={14} strokeWidth={2} aria-hidden className={`${styles.chevron} ${open ? styles.open : ''}`} />
            <Typography variant="p3" color="secondary" component="span">
              {actions.length === 1
                ? t('connectors.meetings.past.actionCountOne')
                : t('connectors.meetings.past.actionCount', { count: actions.length })}
              {/* "· 1 done" — how many the reader has ticked in `notes.md`, which the history row mirrors. */}
              {done > 0 && ` · ${t('connectors.meetings.past.doneCount', { count: done })}`}
            </Typography>
          </button>
        )}
      </div>

      {!meeting.noNotes && actions.length > 0 && (
        <Collapsible open={open} id={panelId}>
          {/* Named for a screen reader entering it — the toggle above is the only other thing
                that says what this list is, and it scrolls out of the announcement. */}
          <ul className={styles.actionList} aria-label={t('connectors.meetings.past.actions')}>
            {actions.map((note, index) => (
              <li key={`${meeting.meetingSessionId}:${index}`} className={styles.actionItem}>
                <SelectionIndicator checked={!!note.done} shape="checkbox" className={styles.mark} />
                <Typography variant="p2" color="primary" component="span" className={note.done ? styles.done : undefined}>
                  {note.text}
                </Typography>
                {attribution(note) && (
                  <Typography variant="p3" color="secondary" component="span" className={styles.attribution}>
                    {attribution(note)}
                  </Typography>
                )}
              </li>
            ))}
          </ul>
        </Collapsible>
      )}
    </li>
  );
}

/** `now` is the connected view's own clock, so a heading turns from "Today" to "Yesterday" at midnight while the tab sits open. */
export function MeetingHistorySection({ now }: { now: number }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { logPageClickBi } = useGenericBi();
  const { meetings, isLoading, isError, hasMore, isLoadingMore, loadMore } = useMeetingHistory();
  useLoadBi('meeting-history', { isLoading: isLoading || isLoadingMore, isError });

  // Both states borrow the copy the Channels card already ships in all 37 bundles, because they are
  // the same two sentences about the same list.
  //
  // Only when NOTHING loaded. A failed "Show earlier meetings" also raises `isError`, and swapping
  // ten meetings the reader is looking at for one error line is worse than the failure — the rows
  // stay, and the button is still there to press again.
  if (isLoading || (isError && meetings.length === 0)) {
    return (
      <section className={styles.section}>
        <Typography variant="p2" color="secondary" component="p">
          {t(isError ? 'connectors.meetings.error' : 'connectors.meetings.loading')}
        </Typography>
      </section>
    );
  }

  // An open tab over nothing has to say so: a blank panel under a selected "Past meetings" reads as a
  // list that failed to load.
  if (meetings.length === 0) {
    return (
      <section className={styles.section}>
        <Typography variant="p2" color="secondary" component="p">
          {t('connectors.meetings.past.empty')}
        </Typography>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      {groupByDay(meetings, meetingStartedAt).map((group) => (
        <div key={group.day} className={styles.dayGroup}>
          <Typography variant="p3" color="secondary" component="h3" className={styles.dayHeading}>
            {meetingDayHeading(group.day, now, locale)}
          </Typography>
          <ul className={styles.list}>
            {/* The newest meeting opens with its items showing, as the design draws it; the rest start folded. */}
            {group.items.map((meeting) => (
              <MeetingHistoryRow key={meeting.meetingSessionId} meeting={meeting} locale={locale} initiallyOpen={meeting === meetings[0]} />
            ))}
          </ul>
        </div>
      ))}
      {/* A failed "Show earlier meetings" keeps the rows it already has, so the only thing left to
          say it failed is here. Without it the button re-enables with its old label and the reader
          cannot tell a failed request from a click that did nothing. Announced, because the rows
          above do not change and a screen reader would otherwise report nothing at all. */}
      {isError && meetings.length > 0 && (
        <Typography variant="p3" color="secondary" component="p" role="alert" className={styles.moreError}>
          {t('connectors.meetings.error')}
        </Typography>
      )}
      {hasMore && (
        <div className={styles.more}>
          <TextButton
            size="small"
            disabled={isLoadingMore}
            onClick={() => {
              logPageClickBi({ page_name: PAGE, button_name: 'show_earlier_meetings' });
              loadMore();
            }}
          >
            {t('connectors.meetings.past.showEarlier')}
          </TextButton>
        </div>
      )}
    </section>
  );
}
